import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { useSystemConfig } from '../hooks/useSystemConfig.js';
import SystemConfigField from '../components/SystemConfigField.jsx';
import Spinner from '../components/Spinner.jsx';
import { RefreshCw, Webhook, Bell, ExternalLink } from 'lucide-react';

const N8N_BASE = 'https://n8n.mainnet.com.ar';

const N8N_WORKFLOWS = [
  {
    id: 'auFgaelmLMkBcPfs',
    name: 'Renovar tokens OAuth',
    description: 'Renueva el access_token de MercadoPago usando el refresh_token antes de que expire. Se ejecuta automáticamente para todos los clientes con OAuth activo.',
    icon: RefreshCw,
    cron: 'Diario 03:00',
    active: true,
  },
  {
    id: 'hh9E90OYUfn9IDWS',
    name: 'Reprocesar webhooks huérfanos',
    description: 'Reintenta asociar y reenviar los webhook_events que llegaron sin orden en la BD local (race condition). Mantiene la conciliación de pagos consistente.',
    icon: Webhook,
    cron: 'Cada 5 min',
    active: true,
  },
  {
    id: 'CsIBzJHzpq8628cf',
    name: 'Alertar tokens por vencer',
    description: 'Verifica si hay tokens OAuth de clientes que vencen en las próximas 48hs y dispara alertas para renovación proactiva.',
    icon: Bell,
    cron: 'Diario 03:00',
    active: true,
  },
];

const TOOLS = [
  {
    name: 'n8n',
    description: 'Automatización de workflows: renovación de tokens OAuth, reproceso de webhooks huérfanos.',
    url: N8N_BASE,
    badge: 'Automatización',
    badgeColor: 'bg-purple-900/40 text-purple-300 border border-purple-700/50',
  },
];

const APIS = [
  { name: 'POS API (mp-pos-api)', url: 'https://mp.mainnet.com.ar/api/health' },
  { name: 'Admin API (mp-admin-api)', url: 'https://mpadmin.mainnet.com.ar/health' },
];

function StatusDot({ ok, loading }) {
  if (loading) return <span className="w-2 h-2 rounded-full bg-slate-600 animate-pulse inline-block" />;
  return <span className={`w-2 h-2 rounded-full inline-block ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />;
}

function ApiStatus({ name, url }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['health', name],
    queryFn: async () => {
      const res = await fetch(url);
      return res.json();
    },
    refetchInterval: 30_000,
    retry: 1,
  });

  const ok = !isError && data?.status === 'ok';

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <StatusDot ok={ok} loading={isLoading} />
        <span className="text-sm text-slate-200">{name}</span>
      </div>
      <span className={`text-xs px-2 py-0.5 rounded font-medium ${ok ? 'text-emerald-300' : isLoading ? 'text-slate-500' : 'text-red-300'}`}>
        {isLoading ? 'verificando…' : ok ? 'online' : 'error'}
      </span>
    </div>
  );
}

export default function Settings() {
  const configQuery = useSystemConfig();

  return (
    <div className="space-y-6">
      <div className="max-w-2xl space-y-6">
      <h2 className="text-xl font-semibold">Configuración del sistema</h2>

      {/* Configuracion global de plataforma */}
      <div className="card space-y-4">
        <h3 className="font-medium text-slate-300">Configuración global de plataforma</h3>
        <p className="text-xs text-slate-500">
          Estos valores afectan todas las llamadas a MercadoPago. Se requiere test antes de guardar cualquier cambio.
        </p>
        {configQuery.isLoading ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : (
          <div className="space-y-3">
            {(configQuery.data || []).map(cfg => (
              <SystemConfigField
                key={cfg.key}
                configKey={cfg.key}
                description={cfg.description}
                hasValue={cfg.has_value}
                valuePreview={cfg.value_preview}
                updatedAt={cfg.updated_at}
              />
            ))}
          </div>
        )}
      </div>

      {/* Estado de los servicios */}
      <div className="card space-y-1">
        <h3 className="font-medium text-slate-300 mb-3">Estado de servicios</h3>
        <div className="divide-y divide-border">
          {APIS.map((api) => (
            <ApiStatus key={api.name} {...api} />
          ))}
        </div>
      </div>

      {/* Herramientas externas */}
      <div className="card space-y-4">
        <h3 className="font-medium text-slate-300">Herramientas</h3>
        <div className="space-y-3">
          {TOOLS.map((tool) => (
            <div key={tool.name} className="flex items-start justify-between gap-4 p-3 bg-bg-elevated rounded-lg border border-border">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">{tool.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${tool.badgeColor}`}>{tool.badge}</span>
                </div>
                <p className="text-sm text-slate-400">{tool.description}</p>
              </div>
              <a
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-sm text-accent hover:text-accent-hover transition-colors flex items-center gap-1"
              >
                Abrir
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Flujos n8n */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium" style={{ color: 'var(--text-secondary)' }}>Flujos n8n — MP Gateway</h3>
          <a
            href={N8N_BASE}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs flex items-center gap-1 hover:text-accent transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            Abrir n8n
            <ExternalLink size={11} />
          </a>
        </div>
        <div className="space-y-2">
          {N8N_WORKFLOWS.map((wf) => {
            const Icon = wf.icon;
            return (
              <a
                key={wf.id}
                href={`${N8N_BASE}/workflow/${wf.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-xl border transition-all group hover:border-accent/40"
                style={{
                  background: 'var(--bg-elevated)',
                  borderColor: 'var(--border)',
                  textDecoration: 'none',
                }}
              >
                {/* Icon */}
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon size={16} className="text-purple-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium group-hover:text-accent transition-colors"
                          style={{ color: 'var(--text-primary)' }}>
                      {wf.name}
                    </span>
                    {wf.active && (
                      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/20 text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        activo
                      </span>
                    )}
                    <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400 font-mono ml-auto">
                      {wf.cron}
                    </span>
                  </div>
                  <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                    {wf.description}
                  </p>
                  <p className="text-xs font-mono mt-1.5 opacity-50 group-hover:opacity-80 transition-opacity"
                     style={{ color: 'var(--text-muted)' }}>
                    {N8N_BASE}/workflow/{wf.id}
                  </p>
                </div>

                {/* Arrow */}
                <ExternalLink size={14} className="shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-accent" />
              </a>
            );
          })}
        </div>
      </div>

      {/* Endpoints n8n */}
      <div className="card space-y-3">
        <h3 className="font-medium text-slate-300">Endpoints para n8n</h3>
        <p className="text-sm text-slate-400">Configurá estos workflows en n8n para automatizar el mantenimiento:</p>
        <div className="space-y-2">
          {[
            {
              label: 'Tokens OAuth próximos a vencer',
              method: 'GET',
              path: '/admin/credentials/expiring-soon?hours=48',
              cron: 'Diario 03:00',
            },
            {
              label: 'Renovar token de un cliente',
              method: 'POST',
              path: '/admin/credentials/refresh/:clientId',
              cron: 'Por cada resultado anterior',
            },
            {
              label: 'Reprocesar webhooks huérfanos',
              method: 'POST',
              path: '/admin/webhooks/reprocess-orphans',
              cron: 'Cada 5 minutos',
            },
          ].map((ep) => (
            <div key={ep.path} className="bg-bg-elevated rounded-md px-3 py-2 text-sm border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-mono px-1.5 py-0.5 rounded font-bold ${ep.method === 'GET' ? 'bg-sky-900/50 text-sky-300' : 'bg-emerald-900/50 text-emerald-300'}`}>
                  {ep.method}
                </span>
                <span className="text-slate-300 font-medium">{ep.label}</span>
                <span className="text-slate-500 text-xs ml-auto">{ep.cron}</span>
              </div>
              <code className="text-xs text-slate-400 font-mono">
                https://mpadmin.mainnet.com.ar{ep.path}
              </code>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Auth: header <code className="text-slate-400">X-Admin-Key: &lt;ADMIN_API_KEY del .env&gt;</code>
        </p>
      </div>

      {/* Info del sistema */}
      <div className="card">
        <h3 className="font-medium text-slate-300 mb-3">Información</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-400">POS API</dt>
            <dd className="font-mono text-slate-300">https://mp.mainnet.com.ar</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-400">Admin API</dt>
            <dd className="font-mono text-slate-300">https://mpadmin.mainnet.com.ar/admin</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-400">Panel admin</dt>
            <dd className="font-mono text-slate-300">https://mpadmin.mainnet.com.ar</dd>
          </div>
        </dl>
      </div>
      </div>

    </div>
  );
}
